import React from 'react';
import _ from 'lodash';

import Table from 'components/Table';
import InvoiceStatusTag from 'components/InvoiceStatusTag';

import CheckBox from 'components/CheckBox';
import Paginator from 'components/Paginator';
import ButtonDropdown from 'components/ButtonDropdown';
import ThreeStateCheckBox from 'components/ThreeStateCheckBox';
import BookingStatus from 'components/BookingStatus';

import {SCHEDULE_TYPES_FOR_VISUALIZATION} from 'ducks/reporting';

import {formatDate} from 'utils/datetime';
import {splitListBy} from 'utils/common';

import {exportInvoicesCsv as exportInvoicesCsvSdk} from 'sdk/reporting';

import './styles.css';

interface InvoiceRowTypes {
  id: number;
  uuid: string;
  primaryTenant: string;
  schedule: string;
  idString: string;
  dueDate: string;
  totalIncludingTax: number;
  totalDueIncludingTax: number;
  status: string;
  overdue: number;
  dateIssued: string;
  createdBy: string;
  createdAt: string;
  rentStart?: string;
  rentEnd?: string;
  xeroAccount: string;
  bookingId: number;
  bookingStatus: string;
  xeroLink?: string;
  xeroNumber?: string;
  xeroId?: string;
  selectedInvoicesUuids: Array<string>;
  selectInvoice: (checked: boolean, id: string) => void;
  dueIn: number;
  detailLink: string;
  redirectToBookingDetail: ({bookingId}: {bookingId: number}) => void;
  redirectToScheduleItem: ({link}: {link: string | undefined}) => void;
}

const InvoiceRow = ({
  id,
  uuid,
  primaryTenant,
  schedule,
  idString,
  dueDate,
  totalIncludingTax,
  totalDueIncludingTax,
  status,
  overdue,
  dateIssued,
  createdBy,
  createdAt,
  rentStart,
  rentEnd,
  xeroAccount,
  bookingId,
  bookingStatus,
  xeroLink,
  xeroNumber,
  xeroId,
  selectedInvoicesUuids,
  selectInvoice,
  dueIn,
  detailLink,
  redirectToBookingDetail,
  redirectToScheduleItem
}: InvoiceRowTypes) => {
  return (
    <Table.Row key={id}>
      <Table.Cell>
        <CheckBox
          name={`invoice${uuid}`}
          label=""
          value={_.includes(selectedInvoicesUuids, uuid)}
          onChange={event => {
            selectInvoice(event.target.checked, uuid);
          }}
        />
      </Table.Cell>
      <Table.Cell className="tenant">{primaryTenant}</Table.Cell>
      <Table.Cell className="schedule">
        {SCHEDULE_TYPES_FOR_VISUALIZATION[schedule]}
      </Table.Cell>
      <Table.Cell className="invoiceNumber">
        <div
          className="colab-clickable colab-link"
          onClick={() => redirectToScheduleItem({link: detailLink})}>
          {idString}
        </div>
      </Table.Cell>
      <Table.Cell className="dueDate">{formatDate(dueDate)}</Table.Cell>
      <Table.Cell className="rentStart">
        {rentStart && formatDate(rentStart)}
        {!rentStart && 'N/A'}
      </Table.Cell>
      <Table.Cell className="rentEnd">
        {rentEnd && formatDate(rentEnd)}
        {!rentEnd && 'N/A'}
      </Table.Cell>
      <Table.Cell className="total">£{totalIncludingTax}</Table.Cell>
      <Table.Cell className="outstanding">£{totalDueIncludingTax}</Table.Cell>
      <Table.Cell className="dueIn">
        {dueIn === 0 ? '-' : `${dueIn} days`}
      </Table.Cell>
      <Table.Cell className="status">
        <InvoiceStatusTag overdue={overdue} status={status} />
      </Table.Cell>
      <Table.Cell className="issueDate">{formatDate(dateIssued)}</Table.Cell>
      <Table.Cell className="dateCreated">{formatDate(createdAt)}</Table.Cell>
      <Table.Cell className="createdBy">{createdBy}</Table.Cell>
      <Table.Cell className="account">{xeroAccount}</Table.Cell>
      <Table.Cell className="bookingID">
        <div
          className="colab-clickable colab-link"
          onClick={() => redirectToBookingDetail({bookingId})}>
          {bookingId}
        </div>
      </Table.Cell>
      <Table.Cell className="bookingStatus">
        <BookingStatus status={bookingStatus} />
      </Table.Cell>
      <Table.Cell className="xeroInvoice">
        {xeroId ? (
          <div
            className="xeroLink colab-clickable colab-link"
            onClick={() => redirectToScheduleItem({link: xeroLink})}>
            {xeroNumber} →
          </div>
        ) : (
          <InvoiceStatusTag status={InvoiceStatusTag.NOT_SYNCED} />
        )}
      </Table.Cell>
    </Table.Row>
  );
};

const Actions = ({
  initiateGoCardlessPaymentsFlow,
  exportInvoicesCsv,
  exportingInvoices
}) => (
  <ButtonDropdown
    className="Actions"
    title="Actions"
    menuPosition={ButtonDropdown.MENU_POSITION_TOP_RIGHT}
    isLoading={exportingInvoices}>
    <ButtonDropdown.Option onClick={initiateGoCardlessPaymentsFlow}>
      Initiate GoCardless Charge(s)
    </ButtonDropdown.Option>
    <ButtonDropdown.Option onClick={() => exportInvoicesCsv(false)}>
      Export selected
    </ButtonDropdown.Option>
    <ButtonDropdown.Option onClick={() => exportInvoicesCsv(true)}>
      Export all filtered results
    </ButtonDropdown.Option>
  </ButtonDropdown>
);

class InvoiceReportingTable extends React.Component<any> {
  state = {
    selectedInvoicesUuids: []
  };

  getSelectedInvoicesIds = () =>
    this.props.invoices
      .filter(invoice =>
        _.includes(this.state.selectedInvoicesUuids, invoice.uuid)
      )
      .map(invoice => invoice.id);

  exportInvoicesCsv = (exportAll = false) => {
    const {locationRef, sort, invoicesCsvFilename} = this.props;
    const invoicesIds = exportAll ? [] : this.getSelectedInvoicesIds();
    const filterParams = this.props.filters;

    this.props.exportCsv({
      exportData: {locationRef, invoicesIds, filterParams, sort},
      exportSdk: exportInvoicesCsvSdk,
      fileName: invoicesCsvFilename
    });
  };

  selectInvoice = (checked, invoiceUuid) => {
    const {selectedInvoicesUuids} = this.state;

    if (checked) {
      this.setState({
        selectedInvoicesUuids: [...selectedInvoicesUuids, invoiceUuid]
      });
    }

    if (!checked) {
      this.setState({
        selectedInvoicesUuids: selectedInvoicesUuids.filter(
          (uuid: any) => uuid !== invoiceUuid
        )
      });
    }
  };

  selectAllInvoices = () => {
    const {invoices} = this.props;
    this.setState({selectedInvoicesUuids: invoices.map(inv => inv.uuid)});
  };

  deselectAllInvoices = () => {
    this.setState({selectedInvoicesUuids: []});
  };

  initiateGoCardlessPaymentsFlow = () => {
    const invoicesIds = this.getSelectedInvoicesIds();

    this.props.initiateGoCardlessPaymentsFlow({invoicesIds});
  };

  render() {
    const {
      invoices,
      pagination,
      onPageChange,
      sort,
      sortBy,
      redirectToBookingDetail,
      redirectToScheduleItem,
      exportingInvoices
    } = this.props;

    const {selectedInvoicesUuids} = this.state;

    const [selected, deselected] = splitListBy(invoices, invoice =>
      _.includes(selectedInvoicesUuids, invoice.uuid)
    );

    return (
      <div className="InvoiceReportingTable">
        <div className="tableContainer">
          <div className="tableHeader">
            <Table>
              <Table.Header>
                <Table.HeaderItem>
                  <ThreeStateCheckBox
                    className="selectAll"
                    selected={selected.length > 0}
                    deselected={deselected.length > 0}
                    onSelectAll={this.selectAllInvoices}
                    onDeselectAll={this.deselectAllInvoices}
                  />
                </Table.HeaderItem>
                <Table.HeaderItem
                  className="tenant"
                  heading="to"
                  sortDirection={
                    sort.field === 'tenant' ? sort.direction : null
                  }
                  onSort={() => sortBy('tenant')}
                />
                <Table.HeaderItem className="schedule" heading="schedule" />
                <Table.HeaderItem
                  className="invoiceNumber"
                  heading="invoice no"
                />
                <Table.HeaderItem className="dueDate" heading="due date" />
                <Table.HeaderItem className="rentStart" heading="rent start" />
                <Table.HeaderItem className="rentEnd" heading="rent end" />
                <Table.HeaderItem className="total" heading="total" />
                <Table.HeaderItem
                  className="outstanding"
                  heading="outstanding"
                  sortDirection={
                    sort.field === 'outstanding' ? sort.direction : null
                  }
                  onSort={() => sortBy('outstanding')}
                />
                <Table.HeaderItem className="dueIn" heading="due in" />
                <Table.HeaderItem className="status" heading="status" />
                <Table.HeaderItem className="issueDate" heading="issue date" />
                <Table.HeaderItem
                  className="dateCreated"
                  heading="date created"
                />
                <Table.HeaderItem className="createdBy" heading="created by" />
                <Table.HeaderItem className="account" heading="account" />
                <Table.HeaderItem className="bookingID" heading="booking id" />
                <Table.HeaderItem
                  className="bookingStatus"
                  heading="booking status"
                />
                <Table.HeaderItem
                  className="xeroInvoice"
                  heading="xero invoice"
                />
              </Table.Header>
            </Table>
          </div>
          <div className="tableBody">
            <Table>
              <Table.Body>
                {invoices.map((invoice, index) => (
                  <InvoiceRow
                    key={index}
                    {...invoice}
                    selectedInvoicesUuids={selectedInvoicesUuids}
                    selectInvoice={this.selectInvoice}
                    redirectToBookingDetail={redirectToBookingDetail}
                    redirectToScheduleItem={redirectToScheduleItem}
                  />
                ))}
              </Table.Body>
            </Table>
          </div>
        </div>
        <Paginator
          pagination={pagination}
          onPageChange={onPageChange}
          ActionsComponent={() => (
            <Actions
              initiateGoCardlessPaymentsFlow={
                this.initiateGoCardlessPaymentsFlow
              }
              exportInvoicesCsv={this.exportInvoicesCsv}
              exportingInvoices={exportingInvoices}
            />
          )}
        />
      </div>
    );
  }
}

export default InvoiceReportingTable;
